import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { AppLayout } from "./components/layout/AppLayout";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import AddBikePage from "./pages/AddBikePage";
import AllBikesPage from "./pages/AllBikesPage";
import EditBikePage from "./pages/EditBikePage";
import VehiclesAnalyticsPage from "./pages/VehiclesAnalyticsPage";
import MembersPage from "./pages/MembersPage";
import BranchesPage from "./pages/BranchesPage";
import SalesPage from "./pages/SalesPage";
import AddSalePage from "./pages/AddSalePage";
import SaleDetailPage from "./pages/SaleDetailPage";
import InvoicesPage from "./pages/InvoicesPage";
import InvoiceSettingsPage from "./pages/InvoiceSettingsPage";
import OfficeExpensesPage from "./pages/OfficeExpensesPage";
import FinanceLedgerPage from "./pages/FinanceLedgerPage";
import MessagesPage from "./pages/MessagesPage";

function FullPageLoader({ label }: { label: string }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-paper text-sm text-ink-muted">
      {label}
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === "hydrating") return <FullPageLoader label="Checking your session…" />;
  if (status === "anonymous") return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AnonymousOnly({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === "hydrating") return <FullPageLoader label="Checking your session…" />;
  if (status === "authenticated") return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <AnonymousOnly>
            <LoginPage />
          </AnonymousOnly>
        }
      />
      <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route
        path="/register"
        element={
          <AnonymousOnly>
            <RegisterPage />
          </AnonymousOnly>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="/" element={<DashboardPage />} />
        <Route path="/vehicles" element={<Navigate to="/vehicles/all" replace />} />
        <Route path="/vehicles/add" element={<AddBikePage />} />
        <Route path="/vehicles/all" element={<AllBikesPage />} />
        <Route path="/vehicles/edit/:id" element={<EditBikePage />} />
        <Route path="/vehicles/analytics" element={<VehiclesAnalyticsPage />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/sales/add" element={<AddSalePage />} />
        <Route path="/sales/manage" element={<SalesPage />} />
        <Route path="/sales/:id" element={<SaleDetailPage />} />
        <Route path="/sales/invoices" element={<InvoicesPage />} />
      <Route path="/sales/invoice-settings" element={<InvoiceSettingsPage />} />
        <Route path="/expenses" element={<OfficeExpensesPage />} />
        <Route path="/finance" element={<Navigate to="/finance/ledger" replace />} />
        <Route path="/finance/ledger" element={<FinanceLedgerPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/listings" element={<Navigate to="/vehicles/all" replace />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/branches" element={<BranchesPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
